import { useQuery } from "@tanstack/react-query";

import { crossSellAPI } from "../api/crossSellAPI";
import { CrossSellProduct } from "../types/CrossSell";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const findProductList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  for (const key of [
    "products",
    "cross_sell",
    "crossSell",
    "recommendations",
    "items",
  ]) {
    if (Array.isArray(payload[key])) return payload[key] as unknown[];
  }

  return "data" in payload ? findProductList(payload.data) : [];
};

const normalizeProducts = (payload: unknown): CrossSellProduct[] => {
  const seenIds = new Set<number>();

  return findProductList(payload).reduce<CrossSellProduct[]>((products, item) => {
    if (!isRecord(item)) return products;

    const rawProduct = isRecord(item.product) ? item.product : item;
    const id = Number(rawProduct.id);

    if (!Number.isInteger(id) || id <= 0 || seenIds.has(id)) return products;
    seenIds.add(id);

    products.push({
      category_id: Number(rawProduct.category_id) || 0,
      category_title: String(rawProduct.category_title ?? ""),
      category_title_uz: String(rawProduct.category_title_uz ?? ""),
      category_img: String(rawProduct.category_img ?? ""),
      id,
      title: String(rawProduct.title ?? ""),
      title_uz: String(rawProduct.title_uz ?? rawProduct.title ?? ""),
      title_en:
        typeof rawProduct.title_en === "string" ? rawProduct.title_en : undefined,
      description: String(rawProduct.description ?? ""),
      description_uz: String(
        rawProduct.description_uz ?? rawProduct.description ?? "",
      ),
      image: String(rawProduct.image ?? ""),
      org_price: String(rawProduct.org_price ?? rawProduct.price ?? "0"),
      type: String(rawProduct.type ?? ""),
      org: Number(rawProduct.org) || 0,
      menu_image: String(rawProduct.menu_image ?? rawProduct.image ?? ""),
      modifier_groups: Array.isArray(rawProduct.modifier_groups)
        ? (rawProduct.modifier_groups as CrossSellProduct["modifier_groups"])
        : null,
    });

    return products;
  }, []);
};

export const useGetCrossSellProducts = (
  branchId: number,
  productIds: number[],
) => {
  const requestKey = productIds.join(",");

  const query = useQuery({
    queryKey: ["basket-cross-sell", branchId, requestKey],
    queryFn: async ({ signal }) => {
      const response = await crossSellAPI.getProducts({
        branchId,
        productIds,
        signal,
      });

      return normalizeProducts(response.data);
    },
    enabled: branchId > 0 && productIds.length > 0,
    staleTime: Infinity,
    retry: false,
  });

  return {
    products: query.data ?? [],
    isLoading: query.isPending && query.fetchStatus === "fetching",
    isError: query.isError,
  };
};
